import React, { useState, useEffect } from "react";
import { Eye, EyeOff, UserPlus } from "lucide-react";
import { fetchCurrentUser } from "../../utils/auth";
import Sidebar from "../../components/Nav/AdminSidebar";
export default function CreateUser() {
  const [form, setForm] = useState({
    username: "",
    password: "",
    role: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [passwordFeedback, setPasswordFeedback] = useState([]);
  const [canSubmit, setCanSubmit] = useState(false);
  const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetch(`${BASE_URL}/users/get-role.php`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          const filtered = data.data.filter((role) => parseInt(role.id) !== 1);
          setRoles(filtered);
          setForm((prev) => ({ ...prev, role: filtered[0]?.name || "" }));
        } else {
          setError("Failed to load roles");
        }
      })
      .catch(() => setError("Error fetching roles"));
  }, []);

  const validateUsername = (username) => {
    if (/\s/.test(username)) {
      return "Username cannot contain spaces";
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      return "Username can only contain letters, numbers, _ or -";
    }
    return "";
  };

  const validatePassword = (password) => {
    const feedback = [];
    if (password.length < 8) feedback.push("At least 8 characters");
    if (!/[A-Z]/.test(password)) feedback.push("At least one uppercase letter");
    if (!/[0-9]/.test(password)) feedback.push("At least one number");
    if (!/[!@#$%^&*(),.?\":{}|<>]/.test(password))
      feedback.push("At least one special character");
    return feedback;
  };

  useEffect(() => {
    setUsernameError(validateUsername(form.username));
    setPasswordFeedback(validatePassword(form.password));
    setCanSubmit(
      !validateUsername(form.username) &&
        validatePassword(form.password).length === 0
    );
  }, [form.username, form.password]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); // clear previous

    if (!canSubmit) return;

    const formData = new FormData();
    formData.append("username", form.username);
    formData.append("password", form.password);
    formData.append("role_id", getRoleId(form.role));

    try {
      const response = await fetch(`${BASE_URL}/users/add-admin-user.php`, {
        method: "POST",
        body: formData,
        //   credentials: "include",
      });

      const text = await response.text(); // always parse as text first
      let result;

      try {
        result = JSON.parse(text);
      } catch (jsonError) {
        console.error("Raw response:", text); // show HTML error
        throw new Error("Invalid response from server.");
      }

      if (result.success) {
        alert("Staff created successfully!");
        setForm({ username: "", password: "", role: roles[0]?.name || "" });
      } else {
        setError(result.message || "An error occurred.");
      }
    } catch (err) {
      setError(err.message || "Submission failed.");
    }
  };

  const getRoleId = (roleName) => {
    const found = roles.find((r) => r.name === roleName);
    return found ? found.id : 0;
  };

  useEffect(() => {
    const checkUser = async () => {
      const user = await fetchCurrentUser();
      if (!user.loggedIn) {
        console.warn("User not logged in");
        // Add redirect logic if you want here
      } else {
        setCurrentUser(user);
      }
    };

    checkUser();
  }, []);

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-400 text-lg">Loading user info...</p>
      </div>
    );
  }
  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Sidebar on the left */}
      <Sidebar />

      {/* Content area */}
      <div className="flex-1 p-8 overflow-auto">
        <div className="max-w-2xl mx-auto bg-white shadow-xl rounded-2xl p-10 border border-blue-100">
          <div className="flex items-center mb-6 space-x-3">
            <UserPlus className="text-blue-600 w-6 h-6" />
            <h2 className="text-2xl font-bold text-gray-800">Create Staff</h2>
          </div>

          {error && <div className="text-red-600 text-sm mb-4">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                type="text"
                name="username"
                value={form.username}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 focus:outline-none shadow-sm"
              />
              {usernameError && (
                <p className="text-red-500 text-sm mt-1">{usernameError}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl pr-10 focus:ring-2 focus:ring-blue-400 shadow-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {passwordFeedback.length > 0 ? (
                <ul className="text-red-500 text-sm mt-2 list-disc pl-5">
                  {passwordFeedback.map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
              ) : (
                form.password && (
                  <p className="text-green-600 text-sm mt-2">
                    Password strength: Strong
                  </p>
                )
              )}
            </div>

            {/* Role Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                name="role"
                value={form.role}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 shadow-sm"
              >
                {roles.map((role) => (
                  <option key={role.id} value={role.name}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={!canSubmit}
              className={`w-full py-3 px-4 font-semibold rounded-xl transition text-white ${
                canSubmit
                  ? "bg-blue-600 hover:bg-blue-700 shadow-md"
                  : "bg-gray-300 cursor-not-allowed"
              }`}
            >
              Create User
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
